import React, { useState, Fragment } from 'react';
// src/pages/CreateAgreement.tsx

import { useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, ChevronRight } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Input, Textarea, Select } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { SplitPreview } from '../components/ui/SplitPreview';
import { createAgreement } from '../lib/persistence';
import { CATEGORY_CONFIG, CURRENCIES, OUTCOME_LABELS, type ServiceCategory, type AgreementOutcomeType } from '../types';
// ─── Step 1: Selección de categoría ───────────────────────────────────────────
function CategorySelector({
  value,
  onChange
}: {
  value: ServiceCategory | '';
  onChange: (v: ServiceCategory) => void;
}) {
  return <div data-id="element-291">
      <p className="text-sm font-medium text-gray-700 mb-3" data-id="element-292">
        ¿Qué tipo de servicio ofrecés? *
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-293">
        {(Object.entries(CATEGORY_CONFIG) as [ServiceCategory, (typeof CATEGORY_CONFIG)[ServiceCategory]][]).map(([key, cfg]) => <button key={key} type="button" onClick={() => onChange(key)} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all ${value === key ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-294">
            <span className="text-xl" data-id="element-295">{cfg.icon}</span>
            <span className="text-xs font-medium leading-tight" data-id="element-296">
              {cfg.label}
            </span>
          </button>)}
      </div>
    </div>;
}
// ─── Step 2: Selector de tipo de resultado ────────────────────────────────────
function OutcomeTypeSelector({
  category,
  value,
  onChange
}: {
  category: ServiceCategory;
  value: AgreementOutcomeType | '';
  onChange: (v: AgreementOutcomeType) => void;
}) {
  const availableTypes = CATEGORY_CONFIG[category]?.outcomeTypes || [];
  return <div data-id="element-297">
      <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-298">
        ¿Cómo se confirma que el servicio fue prestado? *
      </p>
      <p className="text-xs text-gray-400 mb-3" data-id="element-299">
        Esto define cómo el cliente confirma que recibió lo acordado.
      </p>
      <div className="space-y-2" data-id="element-300">
        {availableTypes.map(type => <button key={type} type="button" onClick={() => onChange(type)} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${value === type ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-301">
            <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${value === type ? 'border-gray-900 bg-gray-900' : 'border-gray-300'}`} data-id="element-302" />
            <div data-id="element-303">
              <p className="text-sm font-medium text-gray-900" data-id="element-304">
                {OUTCOME_LABELS[type].label}
              </p>
              <p className="text-xs text-gray-400" data-id="element-305">
                {OUTCOME_LABELS[type].description}
              </p>
            </div>
          </button>)}
      </div>
    </div>;
}
// ─── Formulario principal ─────────────────────────────────────────────────────
interface FormData {
  category: ServiceCategory | '';
  outcome_type: AgreementOutcomeType | '';
  title: string;
  description: string;
  provider_name: string;
  provider_email: string;
  client_email: string;
  client_name: string;
  amount: string;
  currency: string;
  service_date: string;
  service_date_end: string;
  service_location: string;
}
const INITIAL: FormData = {
  category: '',
  outcome_type: '',
  title: '',
  description: '',
  provider_name: '',
  provider_email: '',
  client_email: '',
  client_name: '',
  amount: '',
  currency: 'ARS',
  service_date: '',
  service_date_end: '',
  service_location: ''
};
type Step = 'category' | 'details' | 'review';
export function CreateAgreement() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('category');
  const [form, setForm] = useState<FormData>(INITIAL);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  function set(field: keyof FormData, value: string) {
    setForm(p => ({
      ...p,
      [field]: value
    }));
    if (errors[field]) setErrors(p => ({
      ...p,
      [field]: undefined
    }));
  }
  const selectedCategory = form.category ? CATEGORY_CONFIG[form.category as ServiceCategory] : null;
  function validateStep1() {
    if (!form.category) return false;
    if (!form.outcome_type) return false;
    return true;
  }
  function validateStep2() {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.title.trim()) e.title = 'Obligatorio';
    if (!form.client_email.trim()) e.client_email = 'Obligatorio';else if (!/\S+@\S+\.\S+/.test(form.client_email)) e.client_email = 'Email inválido';
    if (!form.amount) e.amount = 'Obligatorio';else if (Number(form.amount) <= 0) e.amount = 'Debe ser mayor a 0';
    setErrors(e);
    return Object.keys(e).length === 0;
  }
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateStep2()) return;
    setLoading(true);
    setApiError('');
    try {
      const agreement = await createAgreement({
        category: form.category as ServiceCategory,
        outcome_type: form.outcome_type as AgreementOutcomeType,
        title: form.title.trim(),
        description: form.description.trim(),
        provider_name: form.provider_name.trim(),
        provider_email: form.provider_email.trim().toLowerCase(),
        client_email: form.client_email.trim().toLowerCase(),
        client_name: form.client_name.trim(),
        amount: Number(form.amount),
        currency: form.currency,
        service_date: form.service_date || undefined,
        service_date_end: form.service_date_end || undefined,
        service_location: form.service_location || undefined
      });
      navigate(`/agreement-created/${agreement.agreement_code}`);
    } catch (err: any) {
      setApiError(err.message || 'Error al crear el acuerdo.');
    } finally {
      setLoading(false);
    }
  }
  return <Layout data-id="element-306">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-id="element-307">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-308">
          <div className="inline-flex w-12 h-12 bg-gray-900 rounded-2xl items-center justify-center mb-4" data-id="element-309">
            <Shield className="w-6 h-6 text-white" data-id="element-310" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-311">
            Crear acuerdo seguro
          </h1>
          <p className="text-gray-400" data-id="element-312">
            Para cualquier servicio. En 3 minutos.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8" data-id="element-313">
          {(['category', 'details'] as Step[]).map((s, i) => <Fragment key={s} data-id="element-314">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${step === s ? 'bg-gray-900 text-white' : i < (['category', 'details'] as Step[]).indexOf(step) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`} data-id="element-315">
                {i + 1}. {s === 'category' ? 'Tu servicio' : 'Los detalles'}
              </div>
              {i < 1 && <ChevronRight className="w-3 h-3 text-gray-300" data-id="element-316" />}
            </Fragment>)}
        </div>

        <Card padding="lg" data-id="element-317">
          {/* ── PASO 1: Categoría ──────────────────────────────────────── */}
          {step === 'category' && <div className="space-y-6 animate-fade-in" data-id="element-318">
              <CategorySelector value={form.category} onChange={v => set('category', v)} data-id="element-319" />

              {form.category && <div className="animate-slide-up" data-id="element-320">
                  <OutcomeTypeSelector category={form.category as ServiceCategory} value={form.outcome_type} onChange={v => set('outcome_type', v)} data-id="element-321" />
                </div>}

              <Button fullWidth size="lg" disabled={!validateStep1()} onClick={() => setStep('details')} data-id="element-322">
                Continuar
                <ChevronRight className="w-4 h-4" data-id="element-323" />
              </Button>
            </div>}

          {/* ── PASO 2: Detalles ───────────────────────────────────────── */}
          {step === 'details' && <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in" data-id="element-324">
              {/* Contexto de la categoría elegida */}
              {selectedCategory && <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl" data-id="element-325">
                  <span className="text-xl" data-id="element-326">{selectedCategory.icon}</span>
                  <div data-id="element-327">
                    <p className="text-xs font-medium text-gray-600" data-id="element-328">
                      {selectedCategory.label}
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-329">
                      Ejemplos:{' '}
                      {selectedCategory.examples.slice(0, 3).join(', ')}
                    </p>
                  </div>
                  <button type="button" onClick={() => setStep('category')} className="ml-auto text-xs text-blue-600 hover:underline" data-id="element-330">
                    Cambiar
                  </button>
                </div>}

              {/* Descripción del servicio */}
              <Input label="¿Qué servicio vas a prestar? *" placeholder={selectedCategory?.placeholder || 'Describí el servicio'} value={form.title} onChange={e => set('title', e.target.value)} error={errors.title} maxLength={120} data-id="element-331" />

              <Textarea label="Descripción detallada" placeholder="Incluí todo lo relevante: duración, alcance, materiales, condiciones, etc." value={form.description} onChange={e => set('description', e.target.value)} rows={4} hint="Cuanto más claro el acuerdo, menos posibilidad de conflicto." data-id="element-332" />

              {/* Precio */}
              <div className="grid grid-cols-2 gap-3" data-id="element-333">
                <Input label="Precio *" type="number" placeholder="5000" value={form.amount} onChange={e => set('amount', e.target.value)} error={errors.amount} prefix="$" min="0" data-id="element-334" />
                <Select label="Moneda" value={form.currency} onChange={e => set('currency', e.target.value)} options={CURRENCIES.map(c => ({
              value: c.value,
              label: c.label
            }))} data-id="element-335" />
              </div>

              {/* Split preview */}
              {Number(form.amount) > 0 && <SplitPreview amount={Number(form.amount)} currency={form.currency} mode="compact" data-id="element-336" />}

              <div className="border-t border-gray-100" data-id="element-337" />

              {/* Fecha del servicio */}
              <div className="grid grid-cols-2 gap-3" data-id="element-338">
                <Input label="Fecha del servicio" type="date" value={form.service_date} onChange={e => set('service_date', e.target.value)} hint="¿Cuándo se presta?" data-id="element-339" />
                <Input label="Fecha fin (si aplica)" type="date" value={form.service_date_end} onChange={e => set('service_date_end', e.target.value)} hint="Para servicios con rango" data-id="element-340" />
              </div>

              <Input label="Lugar o modalidad" placeholder="Ej: Casa del cliente, online por Zoom, taller propio" value={form.service_location} onChange={e => set('service_location', e.target.value)} hint="Presencial u online, como corresponda" data-id="element-341" />

              <div className="border-t border-gray-100" data-id="element-342" />

              {/* Datos del cliente */}
              <Input label="Email del cliente *" type="email" placeholder="cliente@email.com" value={form.client_email} onChange={e => set('client_email', e.target.value)} error={errors.client_email} hint="Le llegará el enlace para revisar y pagar." data-id="element-343" />

              <div className="grid grid-cols-2 gap-3" data-id="element-344">
                <Input label="Nombre del cliente" placeholder="Juan García" value={form.client_name} onChange={e => set('client_name', e.target.value)} data-id="element-345" />
                <Input label="Tu nombre" placeholder="María López" value={form.provider_name} onChange={e => set('provider_name', e.target.value)} data-id="element-346" />
              </div>

              <Input label="Tu email" type="email" placeholder="vos@email.com" value={form.provider_email} onChange={e => set('provider_email', e.target.value)} hint="Para recibir notificaciones del acuerdo." data-id="element-347" />

              {apiError && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600" data-id="element-348">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" data-id="element-349" />
                  {apiError}
                </div>}

              <div className="flex gap-3" data-id="element-350">
                <Button type="button" variant="secondary" onClick={() => setStep('category')} className="flex-1" data-id="element-351">
                  Atrás
                </Button>
                <Button type="submit" loading={loading} className="flex-2 flex-1" icon={<Shield className="w-4 h-4" data-id="element-353" />} data-id="element-352">
                  {loading ? 'Creando...' : 'Crear acuerdo seguro'}
                </Button>
              </div>

              <p className="text-center text-xs text-gray-400" data-id="element-354">
                El cliente recibirá el enlace para revisar y pagar. Hasta que no
                pague, no hay compromiso de ninguna parte.
              </p>
            </form>}
        </Card>
      </div>
    </Layout>;
}